import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { UserContext } from "../../utils/userContext";
import { isSuperAdmin, isCustomerAdmin, isOrganisationAdmin } from "../../utils/roles";
import PageHeader from "../../components/ui/PageHeader";
import PageWrapper from "../../components/ui/PageWrapper";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import StatusBadge from "../../components/ui/StatusBadge";
import AdminBreadcrumb from "../../components/admin/AdminBreadcrumb";
import TeamMembersPanel from "../../components/admin/TeamMembersPanel";
import TimeReportingDashboard from "../../components/admin/TimeReportingDashboard";
import FormModal from "../../components/admin/FormModal";
import OrganisationForm from "../../components/admin/OrganisationForm";

export default function OrganisationDetailPage() {
  const { customerId, organisationId } = useParams();
  const { user } = useContext(UserContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const canEditSettings =
    isSuperAdmin(user) ||
    (isCustomerAdmin(user) && String(user.activeCustomerId) === String(customerId));

  const canManageTeam = isOrganisationAdmin(user);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/admin/organisations/${organisationId}`);
      setData(res.data);
    } catch (err) {
      setError("Failed to load department");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  if (loading) {
    return (
      <PageWrapper>
        <p>Loading…</p>
      </PageWrapper>
    );
  }

  const org = data?.organisation || data?.company;
  if (error || !org) {
    return (
      <PageWrapper>
        <p className="text-red-600">{error || "Department not found"}</p>
      </PageWrapper>
    );
  }

  const customerName = org.customerId?.name || "Customer";

  return (
    <>
      <PageHeader
        title={org.name}
        breadcrumb={
          <AdminBreadcrumb
            items={[
              ...(isSuperAdmin(user)
                ? [{ label: "Customers", to: "/admin/customers" }]
                : []),
              {
                label: customerName,
                to: `/admin/customers/${customerId}`,
              },
              { label: org.name },
            ]}
          />
        }
        actions={
          canEditSettings ? (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              Edit department
            </Button>
          ) : null
        }
      />
      <PageWrapper>
        <TimeReportingDashboard
          scope="organisation"
          scopeId={organisationId}
          showOrgColumn={false}
        />

        <Card className="mb-6 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Slug: </span>
              {org.slug}
            </div>
            <div>
              <span className="text-gray-500">Status: </span>
              <StatusBadge status={org.status || "active"} />
            </div>
            {org.notes && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">Notes: </span>
                {org.notes}
              </div>
            )}
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Team</h3>
          <TeamMembersPanel
            organisationId={organisationId}
            customerId={customerId}
            canWrite={canManageTeam}
          />
        </Card>
      </PageWrapper>

      <FormModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit department"
      >
        <OrganisationForm
          initialValues={org}
          onSubmit={async (values) => {
            await axiosInstance.put(`/admin/organisations/${organisationId}`, values);
            setEditOpen(false);
            fetchData();
          }}
          onCancel={() => setEditOpen(false)}
        />
      </FormModal>
    </>
  );
}
